/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import qs from 'qs';
import { getAlpha } from 'utils/color';
import {
  selectCurrentCase,
  selectCurrentFolder,
  selectCurrentSelectedFileMetaData,
  selectFileToCompare,
  selectIsGlobalPageLookup,
  selectIsLiveStream,
  selectMatchedUrl,
  selectPermissions,
  selectQuery,
  selectTheatreData,
  selectUserId,
  selectSessionStarted,
  selectFilteredDocumentAnnotations,
  selectFilteredDocumentAnnotationsForCompare,
  selectMarkBasedScrolling,
  selectPresentQuery,
  selectHubConnectionId,
  selectShowBundleTabPageWatermark,
  selectShowExternIdPageWatermark,
  selectShowGlobalPagePageWatermark,
  selectShowPrefixPlusGlobalPageWatermark,
  selectHardDisconnectOnlyHearingRoom,
} from 'common/selectors';
import * as actions from '../../redux/actions';
import { setViewerControl } from 'features/case/redux/actions';
import { ZoomType } from '../types';
import { ViewerControl, MouseTool, Mark } from '@prizmdoc/viewer-core';
import AcusoftViewerToolbox from './AcusoftViewerToolbox';
import {
  getScrollContainer,
  makeMarkDisappear,
  setScroll,
  setSearchFunction,
  setMouse,
  zoom,
  getDocumentRotationInfo,
  createAnnotations,
  setOverflowHidden,
} from './utils';
import { openNewBackgroundTab } from 'utils/browser';
import { PAGENUMBER_MARK_TYPE, PRESENT_COLOR, SCROLL_MARK_TYPE } from './constants';
import { useSelector } from 'react-redux';
import { useSendMessages } from './hooks/useSendMessages';
import { useScrollToHighlight } from './hooks/useScrollToHighlight';
import { usePauseDocument } from './hooks/usePauseDocument';
import { usePerfectScrollbarUpdater } from './hooks/usePerfectScrollbarUpdater';
import { useFetchAnnotations } from 'features/viewing/redux/fetchAnnotations';
import { useFetchFileDetails } from 'features/case/redux/fetchFileDetails';
import NotionInterface from './NotionInterface';
import { useClearAnnotations } from 'features/viewing/redux/clearAnnotations';
import { useMarks } from './hooks/useMarks';
import debounce from 'utils/debounce';
import { diff } from 'deep-object-diff';
import { isEmpty } from 'utils/objects';
import { useDispatch } from 'react-redux';
import logger from 'utils/logger';
import { makeCancelable } from 'utils/promises';
// import Split from 'react-split';
// import classNames from 'classnames';
import Logger from 'utils/logger';
import { getGlobalPageText, getStartPageForGlobalPaging } from './AcusoftViewerToolbox/globalPage';
import PausedBanner from './AcusoftViewerToolbox/PausedBanner';
import history from 'common/history';

const AcusoftViewerContainer = ({
  removeInput,
  enableScroll,
  className,
  options,
  viewerReadyHandler,
  pageOpeningHandler,
  pageDisplayedHandler,
  pageListElementRef,
  viewerControlRef,
}: {
  removeInput?: boolean;
  enableScroll?: boolean;
  className: string;
  options: any;
  viewerReadyHandler: () => void;
  pageOpeningHandler: () => void;
  pageDisplayedHandler: () => void;
  pageListElementRef: React.MutableRefObject<any>;
  viewerControlRef: React.MutableRefObject<any>;
}) => {
  return (
    <div
      // ACUSOFT CONTAINER
      style={{
        ...(removeInput && !enableScroll && { pointerEvents: 'none' }),
      }}
      data-pcc-pagelist
      className={className}
      ref={(el) => {
        if (el !== null && !pageListElementRef.current) {
          pageListElementRef.current = el;

          viewerControlRef.current = new ViewerControl(el, options);
          viewerControlRef.current.on('ViewerReady', viewerReadyHandler);
          viewerControlRef.current.on('PageOpening', pageOpeningHandler);
          viewerControlRef.current.on('PageDisplayed', pageDisplayedHandler);
        }
      }}
    />
  );
};

interface AcusoftViewerControlProps {
  fileId: string;
  removeInput?: boolean;
  fileData: any;
  className: string;
  enableScroll?: boolean;
  isFullScreen?: boolean;
  isPreviewMode?: boolean;
  isFileToCompare?: boolean;
  isDocFinder: boolean;
  isQueryParamMatchingAnyFolder: boolean;
  startPageForGlobalPaging: any;
  options: any;
  viewingFilePrivate?: boolean;
  passViewingSessionIdToReceivers?: boolean;
  firstParentTriaBundleFileDetails: any;
  hearingRoomMode?: 'private' | 'public';
  searchHandler?: (searchDetails: any) => void;
  searchFunctionsHandler?: (searchFunctions: any) => void;
  sideSummaryOpen?: boolean;
  fileHasSummary?: boolean;
  fetchViewingSessionIdHandler: () => Promise<void>;
  showAnnotationsInTheatreMode?: boolean;
  copyable?: boolean;
  onViewerReady?: () => void;
  onViewerError?: () => void;
  waitForFirstScaleAfterFit?: boolean;
  enableSidebarAnnotations?: boolean;
  annotationToolsEnabled?: boolean;
  hidePresentButton?: boolean;
}

export default memo(
  forwardRef(
    (
      {
        fileId,
        removeInput,
        fileData,
        className,
        enableScroll,
        isFullScreen,
        isPreviewMode,
        isFileToCompare,
        isDocFinder,
        isQueryParamMatchingAnyFolder,
        startPageForGlobalPaging,
        options,
        viewingFilePrivate,
        passViewingSessionIdToReceivers,
        firstParentTriaBundleFileDetails,
        hearingRoomMode,
        searchHandler,
        searchFunctionsHandler,
        sideSummaryOpen,
        fetchViewingSessionIdHandler,
        showAnnotationsInTheatreMode,
        copyable,
        onViewerReady,
        waitForFirstScaleAfterFit,
        enableSidebarAnnotations,
        annotationToolsEnabled,
        hidePresentButton,
      }: AcusoftViewerControlProps,
      ref,
    ) => {
      const inHearingRoom = window.location.pathname.includes('/public-hearing-room/');
      const fileDataDetails = useMemo(
        () => (fileData?.currentFileDetails ? fileData.currentFileDetails : fileData),
        [fileData],
      );
      const fetchTheatreDataPending = useSelector(
        (state: any) => state.viewing.fetchTheatreDataPending,
      );
      const presentQuery = useSelector(selectPresentQuery);
      const annotationId = useSelector((state: any) => state.matcher.params.annotationId);
      const prevAnnotationIdRef = useRef(null);
      const connectionId = useSelector(selectHubConnectionId);
      const userId = useSelector(selectUserId);
      const permissions = useSelector(selectPermissions);
      const currentCase = useSelector(selectCurrentCase);
      const filteredAnnotations = useSelector(
        isFileToCompare
          ? (state: any) => selectFilteredDocumentAnnotationsForCompare(state)
          : selectFilteredDocumentAnnotations,
      );
      const isLiveStream = useSelector(selectIsLiveStream);
      const currentFolder = useSelector(selectCurrentFolder);
      const fileToCompareData = useSelector(selectFileToCompare);
      let currentFileMetaData = useSelector(selectCurrentSelectedFileMetaData) as any;
      currentFileMetaData = isFileToCompare ? fileDataDetails : currentFileMetaData;
      if (inHearingRoom) currentFileMetaData = fileDataDetails;
      if (!isFileToCompare && !inHearingRoom) {
        const fileDataId = fileDataDetails?.id || fileDataDetails?.fileId;
        if (fileDataId && (!currentFileMetaData?.id || currentFileMetaData.id !== fileDataId)) {
          currentFileMetaData = fileDataDetails;
        }
      }

      const normalizedFileMetaData = useMemo(() => {
        const src: any =
          (isFileToCompare || inHearingRoom ? fileDataDetails : currentFileMetaData) || {};
        const bl = (src.bundleLocations && src.bundleLocations[0]) || {};
        return {
          ...src,
          globalPagePrefix: src.globalPagePrefix || bl.globalPagePrefix,
          tab: src.tab || bl.tab,
          startPage: src.startPage ?? bl.startPage,
          folderFileId: src.folderFileId || bl.folderFileId,
          pageCount: src.pageCount || bl.pageCount || src.pageCount,
        };
      }, [fileDataDetails, inHearingRoom, isFileToCompare, currentFileMetaData]);

      if (!inHearingRoom) currentFileMetaData = normalizedFileMetaData;

      // Use normalized metadata throughout the component
      const hearingRoomStarted = useSelector(selectSessionStarted);
      const prevHearingRoomStartedRef = useRef(null);
      const isGlobalPageLookup = useSelector(selectIsGlobalPageLookup);
      const queryParam = useSelector(selectQuery);
      const currentUrl = useSelector(selectMatchedUrl);
      const isQuickLinksRoute = useMemo(
        () => (currentUrl || window.location.pathname).includes('/quick-links/'),
        [currentUrl],
      );
      const theatreData = useSelector((state: any) =>
        selectTheatreData(state, hearingRoomMode),
      ) as any;

      const markBasedScrolling = useSelector(selectMarkBasedScrolling);
      const showBundleTabPageWatermark = useSelector(selectShowBundleTabPageWatermark);
      const showExternIdPageWatermark = useSelector(selectShowExternIdPageWatermark);
      const showGlobalPagePageWatermark = useSelector(selectShowGlobalPagePageWatermark);
      const showPrefixPlusGlobalPageWatermark = useSelector(
        selectShowPrefixPlusGlobalPageWatermark,
      );
      const [externalId, setExternalId] = useState<string | null>(null);
      const externalIdFileId = fileId || fileDataDetails?.id || fileDataDetails?.fileId;
      const externalIdFolderId = currentFolder?.id;

      const presenterConnectionId = useMemo(() => {
        return (
          (fileId &&
            (theatreData?.messages || []).find(
              (message: any) => message.fileId === fileId && userId === message.presenterId,
            )?.connectionId) ||
          null
        );
      }, [fileId, theatreData?.messages, userId]);
      const hasBeenPresenting = useRef(false);
      const prevPresenterConnectionIdRef = useRef(null);
      const showGlobalPaging = currentFolder && currentFolder.globalPaging;
      const isPresenting = !!presentQuery;
      const isFocusView =
        queryParam?.focus === 'true' ||
        queryParam?.focus === true ||
        queryParam?.focus === '1' ||
        Boolean(queryParam?.focused);
      const allHighlights = useMemo(
        () => (isPreviewMode || isPresenting ? [] : filteredAnnotations),
        [isPreviewMode, isPresenting, filteredAnnotations],
      );
      const prevHightlightsRef = useRef<any>([]);
      const isPresentModePage = currentUrl.includes(`${currentCase.id}/present-`);
      const isTheatreMode = !!hearingRoomMode;
      const [isViewerReady, setIsViewerReady] = useState<false | undefined | true | null>(
        undefined,
      );
      const viewerReadyNotifiedRef = useRef(false);
      const waitForInitialScaleRef = useRef(false);
      const waitForInitialScaleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
      const hardDisconnectOnlyHearingRoom =
        useSelector(selectHardDisconnectOnlyHearingRoom) && isTheatreMode;

      const [selectedMouseTool, setSelectedMouseTool] = useState(MouseTool.Type.PanAndEdit);
      const [marksCreatedFirstTime, setMarksCreatedFirstTime] = useState(false);

      const [, updateState] = React.useState<any>();

      const forceUpdate = React.useCallback(() => updateState({}), []);

      const [hasClickedSummary, setHasClickedSummary] = useState(false);
      const pageListElementRef = useRef<any>(null);
      const viewerControlRef = useRef<any>();
      const initializingRef = useRef<number>(0);
      const hideTimeoutRef = useRef<any>(null);
      const prevDoubleTimestampRef = useRef<number>(0);
      const prevTrippleTimestampRef = useRef<number>(0);
      const toolboxRef = useRef<any>(null);

      const { fetchAnnotations } = useFetchAnnotations();
      const { clearAnnotations } = useClearAnnotations();
      const { fetchFileDetails } = useFetchFileDetails();

      const updatePerfectScrollbar = usePerfectScrollbarUpdater(viewerControlRef);

      const pauseCreateMarkHandlerRef = useRef<any>(null);
      const pauseCreateMarkHandlerRef2 = useRef<any>(null);
      const handleFocusViewHyperlink = useCallback(
        (href: string | undefined) => {
          if (!isFocusView || !href) return false;
          let targetUrl: URL;
          try {
            targetUrl = new URL(href, window.location.origin);
          } catch {
            return false;
          }
          const match = targetUrl.pathname.match(/\/case\/[^/]+\/files\/([^/?#]+)/);
          if (!match) return false;
          const targetFileId = match[1];
          if (!targetFileId) return false;
          const location = history.location || window.location;
          const searchParams = new URLSearchParams(location.search || '');
          searchParams.set('focused', targetFileId);
          searchParams.delete('focus');
          const carryParams = ['goToPage', 'tab', 'startPage', 'pageCount', 'globalPagePrefix'];
          carryParams.forEach((param) => {
            const value = targetUrl.searchParams.get(param);
            if (value) searchParams.set(param, value);
            else searchParams.delete(param);
          });
          const nextSearch = searchParams.toString();
          history.push({
            pathname: location.pathname,
            search: nextSearch ? `?${nextSearch}` : '',
            hash: location.hash || '',
            state: (location as any).state,
          });
          return true;
        },
        [isFocusView],
      );

      const {
        unpauseTheatreModeHandler,
        disconnectPauseTheatreModeHandler,
        pauseTheatreMode,
        isDisconnected,
      } = usePauseDocument(
        viewerControlRef.current,
        isTheatreMode,
        initializingRef,
        selectedMouseTool,
        hardDisconnectOnlyHearingRoom,
      );

      const {
        deleteMarkToBeCreated,
        setMarkToBeUpdated,
        setMarkToBeCreated,
        markToBeUpdated,
        markToBeCreated,
        clearAllMarkButtonTriggers,
      } = useMarks(
        viewerControlRef.current,
        isPresenting,
        isTheatreMode,
        pauseCreateMarkHandlerRef,
        pauseCreateMarkHandlerRef2,
      );

      // Clear markButtonTrigger marks when document search changes
      const docSearch = useSelector((state: any) => state.viewing.docSearch);
      const prevDocSearchRef = useRef(docSearch);
      useEffect(() => {
        if (docSearch !== prevDocSearchRef.current) {
          prevDocSearchRef.current = docSearch;
          if (docSearch) {
            // Clear all markButtonTrigger marks when a new search is initiated
            clearAllMarkButtonTriggers();
          }
        }
      }, [docSearch, clearAllMarkButtonTriggers]);

      useEffect(() => {
        let isActive = true;
        const cachedExternalId = fileDataDetails?.externalId || null;

        setExternalId(cachedExternalId);

        if (!externalIdFileId || isQuickLinksRoute) {
          return () => {
            isActive = false;
          };
        }

        fetchFileDetails({
          fileId: externalIdFileId,
          folderId: externalIdFolderId,
        })
          .then((res: any) => {
            if (isActive) {
              setExternalId(res?.externalId || cachedExternalId || null);
            }
          })
          .catch((error: any) => {
            if (isActive) {
              console.error('Failed to fetch external id for watermark', error);
            }
          });

        return () => {
          isActive = false;
        };
      }, [
        externalIdFileId,
        externalIdFolderId,
        fetchFileDetails,
        fileDataDetails?.externalId,
        isQuickLinksRoute,
      ]);

      const setCurrentMouseTool = useCallback(
        (selMouseTool, isPresent?) => {
          deleteMarkToBeCreated();
          if (viewerControlRef.current)
            setMouse(
              selMouseTool,
              isPresent !== undefined ? isPresent : isPresenting,
              viewerControlRef.current,
            );
        },
        [deleteMarkToBeCreated, isPresenting],
      );

      const { startStopPresentHandler, updateScrollPositionForReceivers, sendFullMessage } =
        useSendMessages({
          passViewingSessionIdToReceivers,
          firstParentTriaBundleFileDetails,
          fileId,
          options,
          viewingFilePrivate,
          viewerControlRef: viewerControlRef,
          setCurrentMouseTool,
          selectedMouseTool,
          deleteMarkToBeCreated,
        });

      const { /*scrollSelectedHighlight,*/ scrollToHighlight } = useScrollToHighlight({
        viewerControl: viewerControlRef.current,
      });

      const viewerControlMouseDownHandler = useCallback(() => {
        deleteMarkToBeCreated();
      }, [deleteMarkToBeCreated]);

      const viewerControlClickedHandler = useCallback(
        (e: any) => {
          if (e.originalEvent.timeStamp - prevTrippleTimestampRef.current < 250) {
            prevDoubleTimestampRef.current = 0;
            prevTrippleTimestampRef.current = 0;
          } else if (e.originalEvent.timeStamp - prevDoubleTimestampRef.current < 250) {
            prevDoubleTimestampRef.current = 0;
            prevTrippleTimestampRef.current = e.originalEvent.timeStamp;
          } else {
            prevDoubleTimestampRef.current = e.originalEvent.timeStamp;
          }

          try {
            // Safely check if mark exists in document
            const allMarks = viewerControlRef.current.getAllMarks();
            const markExists = e.mark && allMarks.some((m: any) => m.uid === e.mark.uid);

            if (markExists) {
              if (e.originalEvent.button !== 2) {
                if (e.mark.uid === markToBeCreated?.uid) return;
                if (e.mark.type === PAGENUMBER_MARK_TYPE) return;
                if (e.mark.type === SCROLL_MARK_TYPE) return;
                if (e.mark.type === Mark.Type.TextHyperlinkAnnotation) {
                  const handledInFocusView = handleFocusViewHyperlink(e.mark.href);
                  if (!handledInFocusView) {
                    openNewBackgroundTab(e.mark.href);
                  }
                } else {
                  // Check if this is a markButtonTrigger mark
                  const isMarkButtonTrigger =
                    e.mark.getData && e.mark.getData('subType') === 'markButtonTrigger';
                  if (isMarkButtonTrigger) {
                    setMarkToBeCreated(e.mark);
                  } else {
                    setMarkToBeUpdated(e.mark);
                  }
                }
              }
            } else if (e.mark) {
              console.warn('Mark clicked but not found in document marks:', { markId: e.mark.uid });
            }
          } catch (error) {
            console.warn('Error checking mark in document:', error, { markId: e.mark?.uid });
          }
        },
        [handleFocusViewHyperlink, markToBeCreated?.uid, setMarkToBeUpdated, setMarkToBeCreated],
      );

      const markSelectionChangedHandler = useCallback(() => {
        const allSelectedHighlights = viewerControlRef.current.getSelectedMarks();

        // Filter out non-user marks from selection
        const filteredHighlights = allSelectedHighlights.filter((mark: any) => {
          const isMarkButtonTrigger =
            mark.getData && mark.getData('subType') === 'markButtonTrigger';
          const isPageNumberMark = mark.type === PAGENUMBER_MARK_TYPE;
          const isScrollMark = mark.type === SCROLL_MARK_TYPE;
          if (isMarkButtonTrigger || isPageNumberMark || isScrollMark) {
            // Automatically deselect non-user marks
            try {
              viewerControlRef.current.deselectMarks([mark]);
            } catch (error) {
              console.warn('Error deselecting mark:', error);
            }
            return false;
          }
          return true;
        });

        // prevent multiselect for highlights
        if (filteredHighlights.length > 1) {
          try {
            // Verify mark is still attached to the document before deselecting
            const allMarks = viewerControlRef.current.getAllMarks();
            const markExists = allMarks.some((m: any) => m.uid === filteredHighlights[0].uid);

            if (markExists) {
              viewerControlRef.current.deselectMarks([filteredHighlights[0]]);
            } else {
              console.warn('Cannot deselect mark: not found in document', {
                markId: filteredHighlights[0].uid,
              });
            }
          } catch (error) {
            console.error('Error deselecting mark:', error, {
              markId: filteredHighlights[0]?.uid,
            });
          }
        } else if (filteredHighlights.length === 0) {
          setMarkToBeUpdated(undefined);
        } else {
          setMarkToBeUpdated(filteredHighlights[0]);
        }
      }, [setMarkToBeUpdated]);

      const setAllTheatreViewerData = useCallback(() => {
        const { scroll, scaleFactor, documentRotation, markObjects = [] } = fileData;

        const parsePageNumber = (value: any) => {
          const numericValue = Number(value);
          return Number.isFinite(numericValue) && numericValue > 0
            ? Math.floor(numericValue)
            : null;
        };

        const getLatestPageNumberFromMarks = (marks: any[]) => {
          if (!Array.isArray(marks) || marks.length === 0) return null;
          for (let i = marks.length - 1; i >= 0; i -= 1) {
            const candidate = marks[i];
            const page =
              parsePageNumber(candidate?.pageNumber) ??
              parsePageNumber(candidate?.data?.pageNumber);
            if (page) {
              return page;
            }
          }
          return null;
        };

        const derivedPageNumber =
          getLatestPageNumberFromMarks(markObjects) ||
          parsePageNumber(fileData?.currentFileDetails?.goToPage);

        if (
          derivedPageNumber &&
          viewerControlRef.current &&
          viewerControlRef.current.pageNumber !== derivedPageNumber
        ) {
          try {
            viewerControlRef.current.setPageNumber(derivedPageNumber);
          } catch (error) {
            logger.ERROR(error);
          }
        }

        const rotate = documentRotation - viewerControlRef.current.getPageRotation();

        const decreseIntitializing = () => {
          setTimeout(() => {
            --initializingRef.current;
            if (initializingRef.current === 0) {
              forceUpdate();
            }
          }, 500);
        };

        const setScaleFactor = () => {
          if (viewerControlRef?.current?.getScaleFactor() !== scaleFactor) {
            viewerControlRef?.current?.setScaleFactor(
              Math.max(
                Math.min(viewerControlRef?.current?.getMaxScaleFactor(), scaleFactor),
                viewerControlRef?.current?.getMinScaleFactor(),
              ),
            );
          }
          decreseIntitializing();
        };

        const setRotation = () => {
          if (rotate) {
            const onRotation = () => {
              decreseIntitializing();
              viewerControlRef.current.off('DocumentRotated', onRotation);
            };
            viewerControlRef.current.on('DocumentRotated', onRotation);
            viewerControlRef.current.rotateDocument(rotate);
          } else {
            decreseIntitializing();
          }
        };

        const _setScroll = () => {
          if (rotate) {
            if (!markBasedScrolling) {
              const centerElementX = getScrollContainer(viewerControlRef.current).clientWidth / 2;
              const centerElementY = getScrollContainer(viewerControlRef.current).clientHeight / 2;
              getScrollContainer(viewerControlRef.current).scrollTo(
                scroll.X * getScrollContainer(viewerControlRef.current).scrollWidth -
                  centerElementX,
                scroll.Y * getScrollContainer(viewerControlRef.current).scrollHeight -
                  centerElementY,
              );
            }

            decreseIntitializing();
          } else {
            if (!markBasedScrolling) {
              setScroll(getScrollContainer(viewerControlRef.current), scroll.X, scroll.Y).then(
                () => {
                  decreseIntitializing();
                },
              );
            } else {
              decreseIntitializing();
            }
          }
        };

        const applyHighlights = () => {
          try {
            const existingMarks = viewerControlRef.current.getAllMarks();

            const markObjectsToDeleteUids = markObjects
              .filter(
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                ({ uid, ...rest }: any) =>
                  Object.keys(rest).length === 0 || rest?.data?.visible === 'false',
              )
              .map((x: any) => x.uid);
            const markObjectsToDelete = existingMarks.filter(({ uid }: any) =>
              markObjectsToDeleteUids.includes(uid),
            );
            // delete some marks
            if (markObjectsToDelete && markObjectsToDelete.length > 0) {
              viewerControlRef.current.deleteMarks(markObjectsToDelete);
            }

            const existingMarksUids = existingMarks.map((x: any) => x.uid);
            const newMarksObjects = markObjects.filter(
              ({ uid, ...rest }: any) =>
                Object.keys(rest).length > 0 && !existingMarksUids.includes(uid),
            );

            // create some new marks
            let newMarks = null;
            if (newMarksObjects.length > 0) {
              try {
                newMarks = viewerControlRef.current.deserializeMarks(newMarksObjects);
              } catch (error) {
                console.error('Error deserializing marks in index.tsx:', error);
                newMarks = [];
              }
            }

            const onlyPresentMarks = newMarks
              ? newMarks.filter(
                  (mark: any) =>
                    mark.type !== SCROLL_MARK_TYPE && mark.type !== PAGENUMBER_MARK_TYPE,
                )
              : [];
            const scrollMarks = newMarks
              ? newMarks.filter((mark: any) => mark.type === SCROLL_MARK_TYPE)
              : [];

            // last one needs not to disappear
            if (onlyPresentMarks.length > 0) {
              if (existingMarks) {
                existingMarks.forEach((mark: any) => {
                  if (
                    mark.fillColor === PRESENT_COLOR &&
                    mark.interactionMode === Mark.InteractionMode.SelectionDisabled &&
                    ((mark.opacity && mark.opacity === 150) ||
                      (typeof mark.opacity === 'undefined' && getAlpha(mark.fillColor) === 255))
                  ) {
                    makeMarkDisappear(mark);
                  }
                });
              }

              onlyPresentMarks
                .slice(0, -1)
                .map(
                  (mark: any) =>
                    mark.interactionMode === Mark.InteractionMode.SelectionDisabled &&
                    makeMarkDisappear(mark),
                );
            }

            if (scrollMarks.length > 0) {
              new Promise<void>((r) => {
                if (scrollMarks.length > 0) {
                  try {
                    const scrollMark = scrollMarks[scrollMarks.length - 1];

                    const scrollContainer = getScrollContainer(viewerControlRef.current);

                    viewerControlRef.current
                      .requestPageAttributes(scrollMark.pageNumber)
                      .then((attributes: any) => {
                        const pageWidth = attributes.width;

                        viewerControlRef.current
                          .scrollToAsync({
                            pageNumber: scrollMark.pageNumber,
                            x:
                              (scrollContainer.scrollWidth - pageWidth) / 2 +
                              scrollMark.boundingRectangle.x,
                            y: scrollMark.boundingRectangle.y,
                          })
                          .then(() => {
                            viewerControlRef.current.deleteMarks([
                              scrollMarks[scrollMarks.length - 1],
                            ]);
                          });
                      });
                  } catch (e) {
                    logger.ERROR(e);
                  } finally {
                    r();
                  }
                } else {
                  r();
                }
              }).then(() => {
                decreseIntitializing();
              });
            } else {
              decreseIntitializing();
            }
          } catch (e) {
            logger.ERROR(e);
            decreseIntitializing();
          }
        };

        initializingRef.current += removeInput ? 3 : 4; //it needs to reach 0 when all is completed
        if (!removeInput) {
          setScaleFactor();
        }
        setRotation();
        _setScroll();
        applyHighlights();
      }, [fileData, forceUpdate, markBasedScrolling, removeInput]);

      const updateScroll = debounce(() => updateScrollPositionForReceivers(), 500);

      const dontUseScrollHandlerRef = useRef(false);

      const scrollHandler = useCallback(() => {
        if (dontUseScrollHandlerRef.current) return;

        updateScroll();
      }, [updateScroll]);

      const scaleChangedHandler = useCallback(
        ({ scaleFactor }: any) => {
          try {
            if (waitForInitialScaleRef.current) {
              waitForInitialScaleRef.current = false;
              if (waitForInitialScaleTimeoutRef.current) {
                clearTimeout(waitForInitialScaleTimeoutRef.current);
                waitForInitialScaleTimeoutRef.current = null;
              }
              setTimeout(() => {
                if (isViewerReady && !viewerReadyNotifiedRef.current) {
                  viewerReadyNotifiedRef.current = true;
                  if (onViewerReady) onViewerReady();
                }
              }, 0);
            }
            setTimeout(() => {
              updateScrollPositionForReceivers({
                scaleFactor,
              });
              // this handler get's removed when changing scale
              dontUseScrollHandlerRef.current = false;
            }, 0);
          } catch (e) {
            logger.ERROR(e);
          }
        },
        [isViewerReady, onViewerReady, updateScrollPositionForReceivers],
      );

      const documentRotatedHandler = useCallback(() => {
        setTimeout(() => {
          updateScrollPositionForReceivers(getDocumentRotationInfo(viewerControlRef.current));
          // this handler get's removed when rotating document
          dontUseScrollHandlerRef.current = false;
        }, 0);
      }, [updateScrollPositionForReceivers]);

      const [highlights, setHighlights] = useState([]);
      const filterHighlightsCancelablePromise = useRef<any>(null);

      const handlePageLoadFailed = useCallback(
        (e: any) => {
          logger.ERROR(e);
          if (e.statusCode === 403) {
            fetchViewingSessionIdHandler().then((viewerOptions) => {
              if (isPresenting) {
                sendFullMessage(viewerOptions);
              }
            });
          }
          Logger.ERROR(e);
        },
        [fetchViewingSessionIdHandler, isPresenting, sendFullMessage],
      );

      const handlePageChangedAnnotations = debounce(
        useCallback(
          ({ pageNumber, reset }: any) => {
            filterHighlightsCancelablePromise.current?.cancel();
            filterHighlightsCancelablePromise.current = makeCancelable(
              new Promise((resolve) => {
                const highlightsToShow = Array.from(
                  new Set([
                    ...(reset ? [] : highlights),
                    ...allHighlights.filter(({ annotation }: any) => {
                      return [Math.max(pageNumber - 1, 0), pageNumber, pageNumber + 1].includes(
                        annotation.pageNumber,
                      );
                    }),
                  ]),
                ) as any;
                resolve(highlightsToShow);
              }).then((highlightsToShow: any) => {
                if (highlightsToShow.length !== highlights.length) setHighlights(highlightsToShow);
              }),
            );
            filterHighlightsCancelablePromise.current.promise.then(() => {});
            toolboxRef.current?.pageNumberChangedHandler({
              pageNumber,
            });
          },
          [allHighlights, highlights],
        ),
        250,
      );

      useEffect(() => {
        try {
          if (!isTheatreMode || showAnnotationsInTheatreMode)
            handlePageChangedAnnotations({
              pageNumber: viewerControlRef.current?.pageNumber,
              reset: true,
            });
        } catch (e) {
          logger.ERROR(e);
        }
      }, [allHighlights]);
      const currentFileMetaDataValue = useMemo(() => {
        // Helper function to check if an object has properties
        const hasProperties = (obj: object) => obj && Object.keys(obj).length > 0;

        // Helper function to check if query params include specific keys
        const queryParamsMissing = (...keys: string[]) =>
          keys.some((key) => !Object.keys(queryParam).includes(key));

        // Helper function to merge metadata with bundle details if available
        const mergeWithBundleDetails = (metadata: any) => {
          const merged = hasProperties(firstParentTriaBundleFileDetails)
            ? { ...metadata, ...firstParentTriaBundleFileDetails }
            : { ...metadata };

          // Add exhibitNumber from queryParam if available
          if (queryParam.exhibitNumber) {
            merged.exhibitNumber = queryParam.exhibitNumber;
          }

          return merged;
        };

        // Case 4: Use query parameters directly when startPage is present, with globalPagePrefix converted to uppercase
        if (Object.keys(queryParam).includes('startPage')) {
          const processedQueryParams = { ...queryParam };

          // Convert globalPagePrefix to uppercase if it exists
          if (processedQueryParams.globalPagePrefix) {
            processedQueryParams.globalPagePrefix =
              processedQueryParams.globalPagePrefix.toUpperCase();
          }
          return mergeWithBundleDetails({ id: fileId, ...processedQueryParams });
        }

        // Case 1: Current file metadata is available and some query params are missing
        if (
          hasProperties(normalizedFileMetaData) &&
          queryParamsMissing('tab', 'globalPagePrefix')
        ) {
          return mergeWithBundleDetails(normalizedFileMetaData);
        }

        // Case 2: Use file data if available
        if (hasProperties(fileDataDetails || {})) {
          return mergeWithBundleDetails(fileDataDetails);
        }

        // Case 3: When query params don't match any folder, return merged metadata
        if (!isQueryParamMatchingAnyFolder) {
          return mergeWithBundleDetails(normalizedFileMetaData || {});
        }
        // Case 5: Fallback to normalized metadata to avoid undefined
        return mergeWithBundleDetails(normalizedFileMetaData);
      }, [
        normalizedFileMetaData,
        queryParam,
        fileData,
        fileDataDetails,
        isQueryParamMatchingAnyFolder,
        firstParentTriaBundleFileDetails,
        fileId,
      ]);

      const shouldShowGlobalPaging =
        showGlobalPaging ||
        (fileData && fileData.showGlobalPaging) ||
        Object.keys(queryParam).includes('startPage') ||
        isGlobalPageLookup ||
        (firstParentTriaBundleFileDetails &&
          Object.keys(firstParentTriaBundleFileDetails).length > 0) ||
        (isPresentModePage &&
          currentFileMetaData &&
          Object.keys(currentFileMetaData).includes('folderId')) ||
        (isDocFinder && currentFileMetaData?.globalPagePrefix) ||
        isQueryParamMatchingAnyFolder ||
        'askAI' in queryParam ||
        'fileAskAI' in queryParam;

      const bundleTabPageMarkRef = useRef(null);
      const externIdPageMarkRef = useRef(null);
      const globalPageMarkRef = useRef(null);

      const addPageNumberMark = useMemo(
        () =>
          debounce((pageNumber: number) => {
            pauseCreateMarkHandlerRef2.current = true;
            const control = viewerControlRef.current;
            if (!control) {
              pauseCreateMarkHandlerRef2.current = false;
              return;
            }

            try {
              if (showBundleTabPageWatermark && bundleTabPageMarkRef.current)
                control.deleteMarks([bundleTabPageMarkRef.current]);
              if (showExternIdPageWatermark && externIdPageMarkRef.current)
                control.deleteMarks([externIdPageMarkRef.current]);
              if (
                (showGlobalPagePageWatermark || showPrefixPlusGlobalPageWatermark) &&
                globalPageMarkRef.current
              )
                control.deleteMarks([globalPageMarkRef.current]);
            } catch {
              //swallow issue
            }

            let promise;
            try {
              promise = control.requestPageAttributes(pageNumber);
            } catch {
              pauseCreateMarkHandlerRef2.current = false;
              return;
            }
            if (!promise || typeof promise.then !== 'function') {
              pauseCreateMarkHandlerRef2.current = false;
              return;
            }

            promise
              .then(
                function (pageAttributes: { width: number; height: number }) {
                  try {
                    if (showBundleTabPageWatermark && !isTheatreMode && shouldShowGlobalPaging) {
                      const bundleTabNumberMark = control.addMark(
                        pageNumber,
                        Mark.Type.TextRedaction,
                      );
                      bundleTabNumberMark.setRectangle({
                        x: 25,
                        y: pageAttributes.height - 25,
                        width: 300,
                        height: 25,
                      });
                      bundleTabNumberMark.interactionMode = Mark.InteractionMode.SelectionDisabled;
                      if (
                        currentFileMetaDataValue &&
                        currentFileMetaDataValue.globalPagePrefix &&
                        currentFileMetaDataValue.tab
                      ) {
                        bundleTabNumberMark.text = `[${currentFileMetaDataValue.globalPagePrefix}/${currentFileMetaDataValue.tab}/${pageNumber}]`;
                      }
                      bundleTabPageMarkRef.current = bundleTabNumberMark;
                    }

                    if (showExternIdPageWatermark && externalId) {
                      const externIdNumberMark = control.addMark(
                        pageNumber,
                        Mark.Type.TextRedaction,
                      );
                      const estimatedTextWidth = Math.max(60, externalId.length * 10);
                      const markWidth = Math.min(
                        pageAttributes.width,
                        Math.max(120, estimatedTextWidth + 20),
                      );
                      const markX = Math.max(
                        Math.min(
                          (pageAttributes.width - estimatedTextWidth) / 2,
                          pageAttributes.width - markWidth,
                        ),
                        0,
                      );
                      externIdNumberMark.setRectangle({
                        x: markX,
                        y: pageAttributes.height - 25,
                        width: markWidth,
                        height: 25,
                      });
                      externIdNumberMark.interactionMode = Mark.InteractionMode.SelectionDisabled;
                      externIdNumberMark.text = externalId;
                      externIdPageMarkRef.current = externIdNumberMark;
                    }

                    if (
                      (showGlobalPagePageWatermark || showPrefixPlusGlobalPageWatermark) &&
                      !isTheatreMode &&
                      shouldShowGlobalPaging
                    ) {
                      const globalPageNumberMark = control.addMark(
                        pageNumber,
                        Mark.Type.TextRedaction,
                      );
                      globalPageNumberMark.setRectangle({
                        x: pageAttributes.width - 75,
                        y: pageAttributes.height - 25,
                        width: 300,
                        height: 25,
                      });
                      globalPageNumberMark.interactionMode = Mark.InteractionMode.SelectionDisabled;
                      if (currentFileMetaDataValue && currentFileMetaDataValue.globalPagePrefix) {
                        const fullText = `${getGlobalPageText(
                          startPageForGlobalPaging,
                          currentFileMetaDataValue,
                          getStartPageForGlobalPaging(
                            startPageForGlobalPaging || currentFileMetaDataValue.startPage,
                          ) +
                            pageNumber -
                            1,
                        )}`;
                        if (showPrefixPlusGlobalPageWatermark) {
                          globalPageNumberMark.text = fullText; // prefix + number (e.g., A150)
                        } else if (showGlobalPagePageWatermark) {
                          const prefix = currentFileMetaDataValue.globalPagePrefix || '';
                          globalPageNumberMark.text = fullText.startsWith(prefix)
                            ? fullText.substring(prefix.length)
                            : fullText; // number only (e.g., 150)
                        }
                      }
                      globalPageMarkRef.current = globalPageNumberMark;
                    }
                  } catch (e) {
                    console.error(e);
                  }
                },
                function (rejectedReason: string) {
                  console.error(
                    'Unable to add annotations because page attributes promise was rejected, error = ' +
                      rejectedReason,
                  );
                },
              )
              .then(() => {
                pauseCreateMarkHandlerRef2.current = false;
              });
          }, 100),
        [
          currentFileMetaDataValue,
          externalId,
          isTheatreMode,
          shouldShowGlobalPaging,
          showBundleTabPageWatermark,
          showExternIdPageWatermark,
          showGlobalPagePageWatermark,
          showPrefixPlusGlobalPageWatermark,
          startPageForGlobalPaging,
        ],
      );

      const handlePageChanged = useCallback(
        ({ pageNumber }: any) => {
          toolboxRef.current?.pageNumberChangedHandler({
            pageNumber,
          });

          const shouldShowGlobalWatermarks =
            !isTheatreMode &&
            shouldShowGlobalPaging &&
            (showBundleTabPageWatermark ||
              showGlobalPagePageWatermark ||
              showPrefixPlusGlobalPageWatermark);
          if (!showExternIdPageWatermark && !shouldShowGlobalWatermarks) return;

          addPageNumberMark(pageNumber);
        },
        [
          addPageNumberMark,
          isTheatreMode,
          shouldShowGlobalPaging,
          showBundleTabPageWatermark,
          showExternIdPageWatermark,
          showGlobalPagePageWatermark,
          showPrefixPlusGlobalPageWatermark,
        ],
      );

      useEffect(() => {
        if (!showExternIdPageWatermark || !externalId) return;
        if (!viewerControlRef.current?.pageNumber) return;

        addPageNumberMark(viewerControlRef.current.pageNumber);
      }, [
        addPageNumberMark,
        externalId,
        showExternIdPageWatermark,
      ]);

      const zoomHandler = useCallback((type: string) => {
        dontUseScrollHandlerRef.current = true;
        zoom(type, viewerControlRef.current);
      }, []);

      const rotateHandler = (angle: number) => {
        dontUseScrollHandlerRef.current = true;
        viewerControlRef.current.rotateDocument(angle);
      };

      const mouseToolHandler = useCallback(
        (toolType: any) => {
          setSelectedMouseTool(toolType);
          setCurrentMouseTool(toolType);
        },
        [setCurrentMouseTool],
      );

      const setPageNumberHandler = (pageNumber: number) => {
        const totalPageCount = viewerControlRef.current.pageCount;
        viewerControlRef.current.setPageNumber(pageNumber <= totalPageCount ? pageNumber : 1);
      };

      const createMarksInViewer = useCallback(
        (highlightsToWorkWith: any = highlights, differenceWithHighlights?: any) => {
          const currentMouseTool = selectedMouseTool;
          try {
            mouseToolHandler(MouseTool.Type.Pan);
            pauseCreateMarkHandlerRef2.current = true;

            createAnnotations(
              viewerControlRef.current,
              highlightsToWorkWith,
              userId,
              differenceWithHighlights,
            ).then((newMarks: any) => {
              if (markToBeUpdated) {
                setMarkToBeUpdated(newMarks.find(({ uid }: any) => uid === markToBeUpdated.uid));
              }
            });
          } catch (e) {
            logger.ERROR(e);
          } finally {
            setTimeout(() => {
              setMarksCreatedFirstTime(true);
              mouseToolHandler(currentMouseTool);
              pauseCreateMarkHandlerRef2.current = false;
            }, 500); // needed for acusoft to stop triggering events
          }
        },
        [
          highlights,
          markToBeUpdated,
          mouseToolHandler,
          selectedMouseTool,
          setMarkToBeUpdated,
          userId,
        ],
      );

      const setReadyWithTimeout = useCallback(
        () =>
          setTimeout(() => {
            setIsViewerReady((prevState: any) => {
              let newState;
              // 4 stage to make it ready
              if (typeof prevState === 'undefined') {
                newState = null;
              } else if (prevState === null) {
                newState = false;
              } else {
                newState = true;
              }
              if (newState) {
                if (presentQuery) {
                  //start presenting if url is present and it's not presenting
                  startStopPresentHandler(presentQuery, true);
                }

                setSearchFunction(viewerControlRef.current, searchHandler, searchFunctionsHandler);
              }

              return newState;
            });
          }, 0),
        [presentQuery, searchFunctionsHandler, searchHandler, startStopPresentHandler],
      );

      const pageOpeningHandler = () => {
        if (!isTheatreMode) {
          // make document go out a bit, no default zoom
          scaleChangedHandler(ZoomType.Out);
        }

        viewerControlRef.current?.off('PageOpening', pageOpeningHandler);
      };

      const pageDisplayedHandler = () => {
        setReadyWithTimeout();

        viewerControlRef.current?.off('PageDisplayed', pageDisplayedHandler);
      };

      const viewerReadyHandler = () => {
        setReadyWithTimeout();
        setOverflowHidden();

        // Dispatch viewerControl to Redux store when ready
        dispatch(setViewerControl(viewerControlRef.current));

        viewerControlRef.current.off('ViewerReady', viewerReadyHandler);
      };

      useEffect(() => {
        if (isViewerReady && !viewerReadyNotifiedRef.current) {
          if (waitForInitialScaleRef.current) return;
          const shouldWaitForScale =
            !!waitForFirstScaleAfterFit && !isTheatreMode && !hasClickedSummary;
          if (shouldWaitForScale) {
            if (!waitForInitialScaleRef.current) {
              waitForInitialScaleRef.current = true;
              if (waitForInitialScaleTimeoutRef.current) {
                clearTimeout(waitForInitialScaleTimeoutRef.current);
              }
              waitForInitialScaleTimeoutRef.current = setTimeout(() => {
                if (waitForInitialScaleRef.current) {
                  waitForInitialScaleRef.current = false;
                  waitForInitialScaleTimeoutRef.current = null;
                  if (isViewerReady && !viewerReadyNotifiedRef.current) {
                    viewerReadyNotifiedRef.current = true;
                    if (onViewerReady) onViewerReady();
                  }
                }
              }, 800);
            }
            return;
          }
          viewerReadyNotifiedRef.current = true;
          if (onViewerReady) onViewerReady();
        }
      }, [hasClickedSummary, isTheatreMode, isViewerReady, onViewerReady, waitForFirstScaleAfterFit]);

      useEffect(() => {
        return () => {
          if (waitForInitialScaleTimeoutRef.current) {
            clearTimeout(waitForInitialScaleTimeoutRef.current);
            waitForInitialScaleTimeoutRef.current = null;
          }
        };
      }, []);


      const pageCountReadyHandler = useCallback(() => {
        updatePerfectScrollbar();
        setTimeout(() => {
          try {
            handlePageChanged({ pageNumber: viewerControlRef.current.pageNumber });
          } catch {
            // TO-DO HIDING ERROR due to timeout set
          }
        }, 250);

        let count = 0;
        const countAllOfThem = () => {
          ++count;
          if (count === highlights.length || isTheatreMode) {
            viewerControlRef.current.off('MarkCreated', countAllOfThem);
            setTimeout(() => {
              setReadyWithTimeout();
            });
          }
        };
        viewerControlRef.current.on('MarkCreated', countAllOfThem);
        if (highlights.length > 0) {
          prevHightlightsRef.current = highlights;
          createMarksInViewer();
        } else {
          --count;
          countAllOfThem();
        }
      }, [
        createMarksInViewer,
        handlePageChanged,
        highlights,
        isTheatreMode,
        setReadyWithTimeout,
        updatePerfectScrollbar,
      ]);

      const dispatch = useDispatch();

      // Cleanup: clear viewerControl when component unmounts
      // Skip for compare viewer to avoid clearing the main viewer's shared control
      useEffect(() => {
        return () => {
          if (!isFileToCompare) {
            dispatch(setViewerControl(null));
          }
        };
      }, [dispatch, isFileToCompare]);

      useEffect(() => {
        hasBeenPresenting.current = hasBeenPresenting.current || isPresenting;
      }, [isPresenting]);

      // mount
      useEffect(() => {
        if (!isPreviewMode || enableSidebarAnnotations) {
          fetchAnnotations({
            file: fileId,
            privateOnly: !!hearingRoomMode,
            forCompare: !!isFileToCompare,
          });
        }

        const vr = viewerControlRef.current;

        // Unmount
        return () => {
          const newUrlQuery =
            qs.parse(window.location.search && window.location.search.substr(1)) || {};

          if (hasBeenPresenting.current && !newUrlQuery.present) {
            dispatch(actions.leaveAGroup('present'));
          }
          clearAnnotations(!!isFileToCompare);
          vr?.destroy();
        };
        // MOUNT
      }, []);

      useEffect(() => {
        viewerControlRef.current.on('PageCountReady', pageCountReadyHandler);
        const vr = viewerControlRef.current;

        return () => {
          vr.off('PageCountReady', pageCountReadyHandler);
        };
      }, [pageCountReadyHandler]);

      useEffect(() => {
        if (!isViewerReady) return;

        const clearMarkToBeUpdated = () => {
          setMarkToBeUpdated(undefined);
        };

        // annotations and highlights
        //added to check vivercontrol is cicked or not
        viewerControlRef.current.on('MarkSelectionChanged', markSelectionChangedHandler);
        viewerControlRef.current.on('Click', viewerControlClickedHandler);
        viewerControlRef.current.on('MouseDown', viewerControlMouseDownHandler);
        getScrollContainer(viewerControlRef.current)?.addEventListener(
          'mousedown',
          deleteMarkToBeCreated as unknown as any,
        );
        getScrollContainer(viewerControlRef.current)?.addEventListener(
          'mousedown',
          clearMarkToBeUpdated,
        );
        if (!isTheatreMode) {
          // document
          getScrollContainer(viewerControlRef.current)?.addEventListener('scroll', scrollHandler);
          viewerControlRef.current.on('ScaleChanged', scaleChangedHandler);
          viewerControlRef.current.on('DocumentRotated', documentRotatedHandler);
        }
        if (!isTheatreMode || showAnnotationsInTheatreMode)
          viewerControlRef.current.on('PageChanged', handlePageChangedAnnotations);

        // when page or scale changes we need to update customToolbox
        viewerControlRef.current.on('PageChanged', handlePageChanged);
        viewerControlRef.current.on('PageLoadFailed', handlePageLoadFailed);

        const vr = viewerControlRef.current;

        return () => {
          vr?.off('MarkSelectionChanged', markSelectionChangedHandler);
          vr?.off('Click', viewerControlClickedHandler);
          vr?.off('MouseDown', viewerControlMouseDownHandler);
          getScrollContainer(vr)?.removeEventListener(
            'mousedown',
            deleteMarkToBeCreated as unknown as any,
          );
          getScrollContainer(vr)?.removeEventListener('mousedown', clearMarkToBeUpdated);
          if (!isTheatreMode) {
            // document
            getScrollContainer(vr)?.removeEventListener('scroll', scrollHandler);
            vr?.off('ScaleChanged', scaleChangedHandler);
            vr?.off('DocumentRotated', documentRotatedHandler);
          }
          if (!isTheatreMode || showAnnotationsInTheatreMode)
            vr?.off('PageChanged', handlePageChangedAnnotations);
          // when page or scale changes we need to update customToolbox
          vr?.off('PageChanged', handlePageChanged);
          vr?.off('PageLoadFailed', handlePageLoadFailed);
        };
      }, [
        deleteMarkToBeCreated,
        documentRotatedHandler,
        handlePageChanged,
        handlePageChangedAnnotations,
        handlePageLoadFailed,
        isTheatreMode,
        isViewerReady,
        markSelectionChangedHandler,
        scaleChangedHandler,
        scrollHandler,
        setMarkToBeUpdated,
        viewerControlClickedHandler,
        viewerControlMouseDownHandler,
      ]);

      useEffect(() => {
        if (
          isViewerReady &&
          isTheatreMode &&
          fileData &&
          !pauseTheatreMode &&
          !initializingRef.current
        ) {
          setAllTheatreViewerData();
        }
      }, [fileData, isTheatreMode, isViewerReady, pauseTheatreMode, setAllTheatreViewerData]);

      useEffect(() => {
        if (isViewerReady && !isTheatreMode) {
          if (
            annotationId &&
            prevAnnotationIdRef.current !== annotationId &&
            allHighlights?.length > 0
          ) {
            prevAnnotationIdRef.current = annotationId;
            const annotation = allHighlights.find(
              (highlight: any) => highlight.id === annotationId,
            );

            if (annotation) {
              scrollToHighlight(annotation);
            }
          }
        }
      }, [
        allHighlights,
        isTheatreMode,
        isViewerReady,
        scrollToHighlight,
        annotationId,
        marksCreatedFirstTime,
      ]);

      // When copyable, switch to text selection (highlight) tool so mouse drag selects text
      useEffect(() => {
        if (isViewerReady && copyable && !isTheatreMode) {
          try {
            mouseToolHandler(MouseTool.Type.HighlightAnnotation);
          } catch (e) {
            logger.ERROR(e);
          }
        }
      }, [copyable, isTheatreMode, isViewerReady, mouseToolHandler]);

      useEffect(() => {
        if (annotationToolsEnabled === false && isViewerReady && !isTheatreMode) {
          try {
            mouseToolHandler(MouseTool.Type.PanAndEdit);
          } catch (e) {
            logger.ERROR(e);
          }
        }
      }, [annotationToolsEnabled, isTheatreMode, isViewerReady, mouseToolHandler]);

      useEffect(() => {
        if (!isViewerReady) return;

        const highlightIds = highlights.map(({ id }: any) => id);
        const highlightsDiffMap = [] as any;
        const highlightsNewMap = [] as any;

        prevHightlightsRef.current.forEach((oldHighlight: any, index: any) => {
          const idIndex = highlightIds.indexOf(oldHighlight.id);
          if (idIndex > -1) {
            highlightIds[idIndex] = null;
            highlightsDiffMap[index] = highlights[idIndex];
            highlightsNewMap[index] = highlights[idIndex];
          } else {
            highlightsDiffMap[index] = undefined;
            highlightsNewMap[index] = oldHighlight;
          }
        });

        highlightIds.filter(Boolean).forEach((id: any) => {
          const idIndex = highlightIds.indexOf(id);
          if (idIndex > -1) {
            highlightsDiffMap[highlightsDiffMap.length] = highlights[idIndex];
            highlightsNewMap[highlightsNewMap.length] = highlights[idIndex];
          }
        });

        const difference = diff(prevHightlightsRef.current, highlightsDiffMap);

        if (!isEmpty(difference)) {
          createMarksInViewer(highlightsNewMap, difference);
          prevHightlightsRef.current = highlights;
        }
      }, [createMarksInViewer, highlights, isViewerReady]);

      useEffect(() => {
        // in case someone else presents
        if (
          isViewerReady &&
          !fetchTheatreDataPending &&
          prevPresenterConnectionIdRef.current !== presenterConnectionId
        ) {
          const shouldIBePresenting = presenterConnectionId === connectionId;
          if (prevPresenterConnectionIdRef.current && isPresenting && !shouldIBePresenting) {
            startStopPresentHandler(presentQuery);
          }
          prevPresenterConnectionIdRef.current = presenterConnectionId;
        }
      }, [
        connectionId,
        fetchTheatreDataPending,
        isPresenting,
        isViewerReady,
        presentQuery,
        presenterConnectionId,
        startStopPresentHandler,
      ]);

      useEffect(() => {
        // in case hearing room is stopped
        if (
          presentQuery === 'public' &&
          prevHearingRoomStartedRef.current !== hearingRoomStarted &&
          !hearingRoomStarted
        ) {
          if (
            ['paused', 'started'].includes(
              prevHearingRoomStartedRef.current as unknown as string,
            ) &&
            hearingRoomStarted === 'stopped'
          ) {
            startStopPresentHandler();
          }
          prevHearingRoomStartedRef.current = hearingRoomStarted;
        }
      }, [
        connectionId,
        fetchTheatreDataPending,
        hearingRoomStarted,
        isViewerReady,
        startStopPresentHandler,
        presenterConnectionId,
        presentQuery,
      ]);

      useImperativeHandle(ref, () => ({
        stopPresent: () => {
          if (presentQuery) {
            startStopPresentHandler();
          }
        },
        getViewerControl: () => {
          return viewerControlRef.current;
        },
        getCurrentPageNumber: () => {
          return viewerControlRef.current?.pageNumber;
        },
        goToPage: (pageNumber: number) => {
          if (viewerControlRef.current && isViewerReady) {
            setPageNumberHandler(pageNumber);
          }
        },
        scrollToPage: (pageNumber: number, x?: number, y?: number) => {
          if (viewerControlRef.current && isViewerReady) {
            return viewerControlRef.current.scrollToAsync({
              pageNumber: pageNumber,
              x: x || 0,
              y: y || 0,
            });
          }
          return Promise.resolve();
        },
      }));

      useEffect(() => {
        if (isViewerReady && !isTheatreMode && !hasClickedSummary) {
          zoomHandler(ZoomType.Page);
          setHasClickedSummary(true);
        }
      }, [hasClickedSummary, isTheatreMode, isViewerReady, sideSummaryOpen, zoomHandler]);

      const goToFirstPage = () => viewerControlRef.current.changeToFirstPage();
      const goToLastPage = () => viewerControlRef.current.changeToLastPage();
      const goToPrevPage = () => viewerControlRef.current.changeToPrevPage();
      const goToNextPage = () => viewerControlRef.current.changeToNextPage();
      return (
        <div
          style={{
            height: '100%',
            width: 'auto',
            display: 'flex',
          }}
        >
          <div
            id="div22"
            style={{
              // width: '100%',
              height: '100%',
              position: 'relative',
              flex: 3,
              overflow: 'auto',
            }}
          >
            {isTheatreMode && (pauseTheatreMode || hardDisconnectOnlyHearingRoom) && (
              <PausedBanner
                unpauseTheatreMode={unpauseTheatreModeHandler}
                disconnectPauseTheatreMode={disconnectPauseTheatreModeHandler}
                hardDisconnectOnlyHearingRoom={hardDisconnectOnlyHearingRoom}
                isDisconnected={isDisconnected}
              />
            )}
            {!removeInput && isViewerReady && !isLiveStream && pageListElementRef.current && (
              <AcusoftViewerToolbox
                ref={toolboxRef}
                viewerContainer={pageListElementRef.current}
                isTheatreMode={isTheatreMode}
                isFileToCompare={isFileToCompare}
                isFullScreen={isFullScreen}
                fileToCompareData={fileToCompareData}
                presenter={fileData?.presenter}
                fileId={fileId}
                presentMode={presentQuery}
                currentScaleFactor={viewerControlRef?.current?.getScaleFactor()}
                pageCount={viewerControlRef.current.pageCount}
                zoom={zoomHandler}
                rotate={rotateHandler}
                mouseTool={mouseToolHandler}
                selectedMouseTool={selectedMouseTool}
                goToFirstPage={goToFirstPage}
                goToLastPage={goToLastPage}
                goToPrevPage={goToPrevPage}
                goToNextPage={goToNextPage}
                setPageNumber={setPageNumberHandler}
                present={startStopPresentHandler}
                permissions={permissions}
                privateFile={viewingFilePrivate}
                showGlobalPaging={shouldShowGlobalPaging}
                currentFileMetaData={currentFileMetaDataValue}
                isPresentModePage={isPresentModePage}
                isPreviewMode={isPreviewMode}
                startPageForGlobalPaging={startPageForGlobalPaging}
                userId={userId}
                readOnly={hardDisconnectOnlyHearingRoom && !isDisconnected}
                copyable={copyable}
                hidePresentButton={hidePresentButton}
              />
            )}
            <span style={{ visibility: isViewerReady ? 'visible' : 'hidden' }}>
              <AcusoftViewerContainer
                removeInput={removeInput || (hardDisconnectOnlyHearingRoom && !isDisconnected)}
                enableScroll={enableScroll && !(hardDisconnectOnlyHearingRoom && !isDisconnected)}
                className={className}
                options={options}
                viewerReadyHandler={viewerReadyHandler}
                pageOpeningHandler={pageOpeningHandler}
                pageDisplayedHandler={pageDisplayedHandler}
                pageListElementRef={pageListElementRef}
                viewerControlRef={viewerControlRef}
              />
            </span>

            {isViewerReady && !isPresenting && (
              <NotionInterface
                viewerControl={viewerControlRef.current}
                isPresenting={isPresenting}
                markToBeCreated={markToBeCreated}
                markToBeUpdated={markToBeUpdated}
                highlights={allHighlights}
                isTheatreMode={isTheatreMode}
                fileId={fileId}
                isFileToCompare={!!isFileToCompare}
                deleteMarkToBeCreated={deleteMarkToBeCreated}
                setMarkToBeUpdated={setMarkToBeUpdated}
                setMarkToBeCreated={setMarkToBeCreated}
                hideTimeoutRef={hideTimeoutRef}
                pauseCreateMarkHandlerRef={pauseCreateMarkHandlerRef}
                userId={userId}
                copyable={copyable}
              />
            )}
          </div>
        </div>
      );
    },
  ),
);
